/*
 * Hook do trial v2 por dias (TRIAL_V2_API.md) — onboarding in-chat web.
 *
 * - Gera/persiste UUID v4 anônimo em localStorage ('drpera-anon-id')
 * - Consulta GET /api/trial/state (X-Anon-Id se anônimo, Bearer se logado)
 * - Cache em memória (module-level) + refresh() após cada chat
 *
 * PRIMA NON NUOCERE: flag off / 404 / falha de rede → flagOn=false e a app
 * se comporta exatamente como hoje (login-first).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchTrialState, TRIAL_STATE_OFF, type TrialStateResult } from '../api/trial';
import { getOrCreateAnonId } from '../utils/anonId';

/** Cache em memória — sobrevive a remounts na mesma sessão da página */
let cachedState: TrialStateResult | null = null;

export interface UseTrialReturn extends TrialStateResult {
  /** UUID v4 anônimo persistido (sempre disponível, mesmo logado) */
  anonId: string;
  /** true enquanto a 1ª consulta a /api/trial/state está em voo */
  loading: boolean;
  /** Reconsulta o estado no backend (chamar após cada chat) */
  refresh: () => Promise<void>;
}

/**
 * @param loggedIn - true quando há JWT válido (muda a identidade da consulta).
 *                   Mudança de valor re-dispara a consulta (login/logout).
 */
export function useTrial(loggedIn: boolean): UseTrialReturn {
  // anonId é estável durante a vida do componente
  const anonIdRef = useRef<string>(getOrCreateAnonId());

  const [state, setState] = useState<TrialStateResult>(
    cachedState ?? TRIAL_STATE_OFF,
  );
  const [loading, setLoading] = useState<boolean>(cachedState === null);

  const refresh = useCallback(async () => {
    const result = await fetchTrialState(anonIdRef.current);
    cachedState = result;
    setState(result);
    setLoading(false);
  }, []);

  // Consulta inicial + reconsulta quando login/logout muda a identidade.
  useEffect(() => {
    // Cache quente só vale pro primeiro mount — mudança de identidade
    // (login/logout) sempre reconsulta.
    void refresh();
  }, [refresh, loggedIn]);

  return {
    ...state,
    anonId: anonIdRef.current,
    loading,
    refresh,
  };
}
